/* eslint-disable import/no-unresolved */
import { CloseIcon } from "@chakra-ui/icons"
import { AspectRatio, Box, Flex } from "@chakra-ui/react"
import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock"
import { BynderImage } from "components/Image/BynderImage"
import { ProductCardBadgeLabel } from "components/ProductCard/ProductCardBadgeLabel"
import { ProductCardLabel } from "components/ProductCard/ProductCardLabel"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ContractIcon,
  ExpandIcon,
  PlayIcon,
} from "components/SvgIcons"
import { VideoPlayer } from "components/VideoPlayer"
import { useWindowSize } from "hooks"
import { useScreenViewport } from "lib/checkViewport"
import { Logger } from "lib/logger"
import { colours } from "@shared/ui.design-system"
import Image from "next/image"
import React, { useEffect, useRef, useState } from "react"
import { withErrorBoundary } from "react-error-boundary"
import type { Swiper as SwiperType } from "swiper"
import SwiperCore, { Lazy, Navigation, Pagination, Thumbs, Zoom } from "swiper"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/thumbs"
import "swiper/css/zoom"
import { Swiper, SwiperSlide } from "swiper/react"
import { getStaticFile } from "utils/getStaticFile"
import { trackProductImageViewed } from "utils/gtm"
import { isStorybook } from "utils/storybookUtils"

import { GiftMembershipProductCard } from "components/ProductCard/GiftMembershipProductCard"
import { ShareProductPDP } from "components/Shop/PDPPage/PDPHeader/CoreProductInfo/SharePDP"
import { useVariantIds } from "hooks/useVariantIds"
import { dispatch } from "lib/events"
import type { VariantType, VideoAssetType, VisualBynderType } from "types"
import {
  CloseButton,
  MainSwiper,
  SwiperBox,
  SwiperNavButton,
  ThumbsContainer,
  ZoomButton,
  ZoomContainer,
} from "./ProductGalleryStyles"

import { cloudinaryLoader } from "packages/ui-shared/components/Image/loaders/cloudinary-loader"
import { isTouchDevice, isVideoType, sortMedia } from "./ProductGalleryUtils"

export type ProductGalleryProps = {
  variant: VariantType
  useCloudinary?: boolean
}

export type GalleryAssetType = VideoAssetType | VisualBynderType | null

SwiperCore.use([Lazy, Navigation, Zoom, Thumbs, Pagination])

/* istanbul ignore next */
export const ProductGalleryComponentV2: React.FC<ProductGalleryProps> = ({
  variant,
  useCloudinary = false,
}) => {
  const { media, availability, badges, videos } = variant
  const galleryVideos =
    videos?.filter((video) => video?.videoType === "gallery") ?? []
  const galleryImages = media ?? []
  const galleryAssets: GalleryAssetType[] = sortMedia(
    galleryVideos,
    galleryImages
  )

  const [mediaList, setMediaList] = useState((galleryAssets || []).slice(0, 2))
  const prevRef = useRef<HTMLDivElement>(null)
  const nextRef = useRef<HTMLDivElement>(null)
  const container = useRef<HTMLDivElement>(null)
  const zoomButtonRef = useRef<HTMLDivElement>(null)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)
  const [zoomSwiper, setZoomSwiper] = useState<SwiperType | null>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomIndex, setZoomIndex] = useState(-1)
  const { width } = useWindowSize()
  const { isMobile } = useScreenViewport()
  const [isTouchscreen, setIsTouchscreen] = useState(true)

  const thumbsSize = isMobile ? 80 : 96

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setMediaList(galleryAssets || []), [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        hideZoomMode()
      }
    }

    const gestureStart = (e: Event) => {
      e.preventDefault()
    }

    document.addEventListener("gesturestart", gestureStart)
    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("gesturestart", gestureStart)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (zoomIndex > -1) {
      const galleryElem = document.querySelector("#PDPGallery")
      disableBodyScroll(galleryElem)
    } else {
      clearAllBodyScrollLocks()
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [zoomIndex])

  const hideZoomMode = () => {
    setThumbsSwiper(null)
    setIsZoomed(false)
    setZoomIndex(-1)
  }

  const onZoomChange = (_swiper, scale) => {
    setIsZoomed(scale !== 1)
  }

  const { giftMembershipProductId } = useVariantIds()

  const stylingProps = {
    sizes: "(max-width: 768px) 5.6rem, (max-width: 1200px) 6rem",
    objectFit: "contain",
  }

  useEffect(() => {
    setIsTouchscreen(isTouchDevice())
  }, [])

  return (
    <AspectRatio ratio={1} width="100%">
      {variant.id === giftMembershipProductId ? (
        <GiftMembershipProductCard
          imageSrc={variant.visuals[0].url}
          props={stylingProps}
        />
      ) : (
        <SwiperBox
          id="PDPGallery"
          position="relative"
          width="100%"
          height="100%"
          ref={container}
          css={{
            ".swiper-pagination-bullet-active": {
              backgroundColor: colours.accent.deep,
            },
            ".swiper-button-next, .swiper-button-prev": {
              background: "transparent",
            },
            transform: zoomIndex > -1 ? "inherit" : "translateZ(0)",
          }}
          zIndex="inherit"
        >
          <MainSwiper>
            <Swiper
              onInit={(swiper) => {
                if (
                  typeof swiper.params.navigation !== "boolean" &&
                  swiper.params.navigation
                ) {
                  const { navigation } = swiper.params
                  navigation.prevEl = ".swiper-button-prev"
                  navigation.nextEl = ".swiper-button-next"
                }
                swiper.navigation.update()
              }}
              navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              }}
              pagination
              zoom
              centeredSlides
              centeredSlidesBounds
              slidesPerView="auto"
              spaceBetween={0}
              onClick={(swiper, event) => {
                const { className } = event.target as HTMLElement
                if (className.startsWith("vjs-")) return
                if (
                  !["swiper-button-next", "swiper-button-prev"].includes(
                    className
                  )
                ) {
                  setZoomIndex(swiper.clickedIndex)
                  trackProductImageViewed()
                }
              }}
              onSlideChangeTransitionEnd={(swiper) => {
                /* ------------------------ Validate next button ------------------------ */
                const isNextButtonDisabled =
                  swiper.navigation.nextEl[0].className.includes(
                    "swiper-button-disabled"
                  )
                if (swiper.progress < 1 && isNextButtonDisabled) {
                  swiper.navigation.update()
                }

                /* ------------------------ Validate previous button ------------------------ */
                const isPrevButtonDisabled =
                  swiper.navigation.prevEl[0].className.includes(
                    "swiper-button-disabled"
                  )
                if (swiper.progress > 0 && isPrevButtonDisabled) {
                  swiper.navigation.update()
                }
              }}
              onSlideChange={(swiper) => {
                dispatch("swiper.onslidechange", {
                  target: "PDP Gallery",
                  position: swiper?.realIndex,
                })
              }}
              touchStartPreventDefault={!isTouchscreen}
              noSwipingClass="vjs-control-bar"
            >
              {mediaList?.map((item, index) => {
                if (isVideoType(item)) {
                  return (
                    <SwiperSlide
                      key={`full-video-player-${item.id}`}
                      data-testid="swiper-slide"
                      style={{
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      <VideoPlayer
                        options={{
                          html5: {
                            nativeTextTracks: false,
                          },
                          playsinline: true,
                          autoplay: false,
                          controls: true,
                          responsive: true,
                          fluid: true,
                          aspectRatio: "1:1",
                          sources: item.sources.map(({ uri, contentType }) => ({
                            src: uri,
                            type: contentType,
                          })),
                        }}
                        description={item.description}
                        videoProps={{
                          title: item.name,
                          "aria-label": item.name,
                          id: "gallery-video-player",
                          className:
                            "video-js vjs-big-play-centered vjs-show-big-play-button-on-pause gallery-video",
                        }}
                        coverImageProps={{
                          src: item.imageUrl,
                          title: item.name,
                          alt: item.name,
                          priority: true,
                          quality: 85,
                          blurDataURL: getStaticFile("/images/placeholder.svg"),
                          sizes: "(max-width: 1024px) 100vw, 50vw",
                          placeholder:
                            index === 0 || isStorybook ? "empty" : "blur",
                        }}
                        videoPosition="PDP gallery"
                        pauseWhenNotInView={true}
                      />
                    </SwiperSlide>
                  )
                }
                return (
                  <SwiperSlide
                    data-testid="swiper-slide"
                    key={`full-${item?.id}`}
                    style={{
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    <Image
                      data-testid="next-image"
                      src={item?.thumbnails?.size_OTF_BETA ?? ""}
                      fill
                      alt={item?.description ?? ""}
                      placeholder={
                        index === 0 || isStorybook ? "empty" : "blur"
                      }
                      blurDataURL={getStaticFile("/images/placeholder.svg")}
                      unoptimized={isStorybook}
                      quality={85}
                      priority
                      sizes="(max-width: 768px) 100vw, (max-width: 1430px) 50vw, 50vw"
                      loader={useCloudinary ? cloudinaryLoader : undefined}
                    />
                  </SwiperSlide>
                )
              })}

              <SwiperNavButton
                data-testid="gallery-prev"
                onClick={() => {
                  trackProductImageViewed()
                }}
                className="swiper-button-prev"
                ref={prevRef}
              >
                <ChevronLeftIcon boxSize="2rem" />
              </SwiperNavButton>
              <SwiperNavButton
                data-testid="gallery-next"
                onClick={() => {
                  trackProductImageViewed()
                }}
                className="swiper-button-next"
                ref={nextRef}
              >
                <ChevronRightIcon boxSize="2rem" />
              </SwiperNavButton>
            </Swiper>
          </MainSwiper>

          {zoomIndex > -1 && (
            <ZoomContainer data-testid="ZoomContainer">
              <Flex pos="relative" height="100%" direction="column">
                <Swiper
                  onZoomChange={onZoomChange}
                  thumbs={{ swiper: thumbsSwiper }}
                  zoom
                  initialSlide={zoomIndex}
                  allowTouchMove={false}
                  onInit={(swiper) => {
                    setZoomSwiper(swiper)
                  }}
                  onSlideChange={(zoomSwiperInstance) => {
                    dispatch("swiper.onslidechange", {
                      target: "PDP Gallery Zoomed",
                      position: zoomSwiperInstance?.realIndex,
                    })
                    const isVideoSlide = isVideoType(
                      mediaList[zoomSwiperInstance.activeIndex]
                    )
                    const displayVal = isVideoSlide ? "none" : "flex"
                    zoomButtonRef.current?.style.setProperty(
                      "display",
                      displayVal
                    )
                  }}
                >
                  {mediaList?.map((item) => {
                    if (isVideoType(item)) {
                      return (
                        <SwiperSlide
                          key={`zoom-video-player${item.id}`}
                          style={{
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <VideoPlayer
                            options={{
                              html5: {
                                nativeTextTracks: false,
                              },
                              playsinline: true,
                              autoplay: false,
                              controls: true,
                              responsive: true,
                              aspectRatio: "1:1",
                              sources: item.sources.map(
                                ({ uri, contentType }) => ({
                                  src: uri,
                                  type: contentType,
                                })
                              ),
                            }}
                            description={item.description}
                            videoProps={{
                              title: item.name,
                              "aria-label": item.name,
                              id: "gallery-video-player",
                              className:
                                "video-js vjs-big-play-centered vjs-show-big-play-button-on-pause gallery-video gallery-video-zoom",
                            }}
                            coverImageProps={{
                              src: item.imageUrl,
                              title: item.name,
                              alt: item.name,
                              style: { objectFit: "contain" },
                            }}
                            height="100%"
                            videoPosition="PDP gallery zoom"
                            pauseWhenNotInView={true}
                          />
                        </SwiperSlide>
                      )
                    }
                    return (
                      <SwiperSlide
                        key={`fullscreen-${item?.id}`}
                        data-testid="swiper-slide"
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <Box
                          className="swiper-zoom-container"
                          data-swiper-zoom="3"
                        >
                          <BynderImage
                            data-testid="bynder-image"
                            src={item?.thumbnails?.size_OTF_BETA ?? ""}
                            alt={item?.description ?? ""}
                            objectFit="contain"
                            boxSize="100%"
                          />
                        </Box>
                      </SwiperSlide>
                    )
                  })}
                </Swiper>

                <ThumbsContainer data-testid="ThumbsContainer">
                  <Flex direction="row">
                    <Swiper
                      onSwiper={setThumbsSwiper}
                      freeMode
                      initialSlide={zoomIndex}
                      spaceBetween={0}
                      slidesPerView={width / thumbsSize - 1}
                      watchSlidesProgress
                    >
                      {mediaList?.length &&
                        mediaList?.map((item) => {
                          if (isVideoType(item)) {
                            return (
                              <SwiperSlide
                                className="thumbs-swiper-slide"
                                key={`video-thumbnail-${item.id}`}
                                style={{
                                  width: thumbsSize,
                                  height: thumbsSize,
                                  position: "relative",
                                }}
                              >
                                <PlayIcon
                                  color={colours.primary.dark}
                                  boxSize="60px"
                                  style={{
                                    position: "absolute",
                                    opacity: 0.5,
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                  }}
                                />
                                <Image
                                  data-testid="next-image"
                                  style={{ objectFit: "contain" }}
                                  src={item.imageUrl}
                                  alt={item.name}
                                  width={thumbsSize}
                                  height={thumbsSize}
                                  unoptimized={isStorybook}
                                  loader={
                                    useCloudinary ? cloudinaryLoader : undefined
                                  }
                                />
                              </SwiperSlide>
                            )
                          }
                          return (
                            <SwiperSlide
                              className="thumbs-swiper-slide"
                              key={`thumbnail-${item?.id}`}
                              style={{
                                width: thumbsSize,
                                height: thumbsSize,
                              }}
                            >
                              <Image
                                data-testid="next-image"
                                style={{ objectFit: "contain" }}
                                src={item?.thumbnails?.size_mini as string}
                                alt={item?.description as string}
                                width={thumbsSize}
                                height={thumbsSize}
                                unoptimized={isStorybook}
                                loader={
                                  useCloudinary ? cloudinaryLoader : undefined
                                }
                              />
                            </SwiperSlide>
                          )
                        })}
                    </Swiper>
                    <CloseButton
                      w={`${thumbsSize}px`}
                      h={`${thumbsSize}px`}
                      minWidth={`${thumbsSize}px`}
                      onClick={hideZoomMode}
                    >
                      <CloseIcon
                        width="1.25rem"
                        height="1.25rem"
                        color={colours.primary.dark}
                      />
                    </CloseButton>
                  </Flex>
                </ThumbsContainer>
              </Flex>

              <ZoomButton
                ref={zoomButtonRef}
                bottom={`${thumbsSize}px`}
                onClick={() => {
                  if (zoomSwiper) {
                    zoomSwiper.zoom.toggle()
                  }
                }}
                display={isVideoType(mediaList[zoomIndex]) ? "none" : "flex"}
              >
                {isZoomed ? (
                  <ContractIcon boxSize="1.5rem" color={colours.primary.dark} />
                ) : (
                  <ExpandIcon boxSize="1.5rem" color={colours.primary.dark} />
                )}
              </ZoomButton>
            </ZoomContainer>
          )}

          {badges?.length ? (
            badges.map(
              (item) =>
                item && (
                  <ProductCardBadgeLabel
                    key={`badge-${item?.badge}`}
                    zIndex={1}
                    mt={0}
                    bottom="initial"
                    position="absolute"
                    top={2}
                    left={0}
                    badge={item}
                    gallery
                  />
                )
            )
          ) : (
            <ProductCardLabel
              zIndex={1}
              mt={0}
              top={2}
              left={2}
              bottom="initial"
              position="absolute"
              availability={availability}
            />
          )}

          <Box
            zIndex={1}
            id="hello"
            mt={0}
            bottom="initial"
            position="absolute"
            top={0}
            display={{ lg: "none" }}
            right={1}
          >
            <ShareProductPDP variant={variant} />
          </Box>
        </SwiperBox>
      )}
    </AspectRatio>
  )
}

export const ProductGalleryV2 = withErrorBoundary(ProductGalleryComponentV2, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`ProductGallery failed to load. ${error}`)
  },
})
